import { computed, Injectable, signal } from '@angular/core';

import { PIP_TYPE } from '../constants';

declare global {
  interface Window {
    documentPictureInPicture?: {
      requestWindow(options?: {
        width?: number;
        height?: number;
        disallowReturnToOpener?: boolean;
      }): Promise<Window>;
      window: Window | null;
    };
  }
}

export const MIN_WIDTH_PIP = 360;
export const MIN_HEIGHT_PIP = 480;
export const LG_WIDTH_PIP = 720;
export const MD_WIDTH_PIP = 540;
export const SM_WIDTH_PIP = 320;

@Injectable({ providedIn: 'root' })
export class PipService {
  private readonly _pipWindow = signal<Window | null>(null);
  readonly pipWindow = this._pipWindow.asReadonly();
  readonly isOpen = computed(() => this._pipWindow() !== null);

  // Placeholder div để giữ chỗ trong DOM khi element đang ở trong PiP
  private placeholder: HTMLElement | null = null;
  // Element gốc và parent gốc để trả về đúng vị trí
  private originalParent: HTMLElement | null = null;
  private originalNextSibling: ChildNode | null = null;
  private resizeHandler: (() => void) | null = null;
  private overlay: HTMLElement | null = null;
  private styleObserver: MutationObserver | null = null;
  readonly typePip = signal('');

  get isSupported(): boolean {
    return 'documentPictureInPicture' in window;
  }

  async openPip(
    element: HTMLElement,
    width = MIN_WIDTH_PIP,
    height = MIN_HEIGHT_PIP,
    type: string = PIP_TYPE.CALL,
  ): Promise<void> {
    if (!this.isSupported || !window.documentPictureInPicture) return;

    this.typePip.set(type);
    this.closePip();
    // Lưu vị trí gốc trong DOM
    this.saveOriginalPosition(element);

    const pipWindow = await window.documentPictureInPicture.requestWindow({ width, height });
    this._pipWindow.set(pipWindow);

    // Copy stylesheets sang PiP window
    this.copyDocumentStyles(pipWindow);
    this.setupStyleObserver(pipWindow);
    this.setupPipWindow(pipWindow);
    pipWindow.document.body.append(element);

    if (type === PIP_TYPE.CALL) {
      this.overlay = this.createOverlay(pipWindow);
      this.setupResizeGuard(pipWindow);
    }

    // Khi PiP đóng (bấm X trên window PiP, hoặc tab đóng) → trả element về
    pipWindow.addEventListener(
      'pagehide',
      () => {
        this.returnElementToOriginalPosition(element);
        this.cleanup(pipWindow);
      },
      { once: true },
    );
  }

  closePip(): void {
    // Gọi close() sẽ trigger pagehide → returnElementToModal tự chạy
    this._pipWindow()?.close();
  }

  snapToMinSize(): void {
    const pipWindow = this._pipWindow();
    if (!pipWindow) return;

    const chromeW = pipWindow.outerWidth - pipWindow.innerWidth;
    const chromeH = pipWindow.outerHeight - pipWindow.innerHeight;

    // Chỉ snap chiều nào đang nhỏ hơn MIN, giữ nguyên chiều đã hợp lệ
    // TODO
    // const targetInnerW = Math.max(pipWindow.innerWidth, MIN_WIDTH_PIP);
    const targetInnerH = Math.max(pipWindow.innerHeight, MIN_HEIGHT_PIP);

    pipWindow.resizeTo(MIN_WIDTH_PIP + chromeW, targetInnerH + chromeH);
    this.hideOverlay();
  }

  private showOverlay(): void {
    this.overlay?.classList.add('pip-overlay--visible');
  }

  private hideOverlay(): void {
    this.overlay?.classList.remove('pip-overlay--visible');
  }

  private setupResizeGuard(pipWindow: Window): void {
    this.resizeHandler = () => {
      const { innerWidth: w, innerHeight: h } = pipWindow;
      if (w < MIN_WIDTH_PIP || h < MIN_HEIGHT_PIP) {
        this.showOverlay();
      } else {
        this.hideOverlay();
      }
    };
    pipWindow.addEventListener('resize', this.resizeHandler);
  }

  private createOverlay(pipWindow: Window): HTMLElement {
    const style = pipWindow.document.createElement('style');
    style.textContent = `
      .pip-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 16px;
        box-sizing: border-box;
        background: rgba(27, 27, 27, 0.85);
        backdrop-filter: blur(8px);
        opacity: 0;
        pointer-events: none;
        transition: opacity 200ms ease;
      }
      .pip-overlay--visible {
        opacity: 1;
        pointer-events: auto;
      }
      .pip-overlay__text {
        color: #fff;
        font-size: 14px;
        line-height: 1.5;
        text-align: center;
        word-break: break-word;
        margin: 0;
      }
      .pip-overlay__btn {
        padding: 6px 20px;
        border-radius: 9999px;
        background: rgba(255,255,255,0.2);
        color: #fff;
        font-size: 14px;
        border: none;
        cursor: pointer;
        white-space: nowrap;
        transition: background 150ms ease;
      }
      .pip-overlay__btn:hover {
        background: rgba(255,255,255,0.3);
      }
    `;
    pipWindow.document.head.append(style);

    const overlay = pipWindow.document.createElement('div');
    overlay.className = 'pip-overlay';
    overlay.innerHTML = `
      <p class="pip-overlay__text">
        Cuộc gọi của bạn vẫn đang diễn ra.<br>Hãy phóng to cửa sổ này.
      </p>
      <button class="pip-overlay__btn">Về kích thước mặc định</button>
    `;

    // Nút nằm trong PiP document — click có user activation, resizeTo hoạt động
    overlay.querySelector('button')!.addEventListener('click', () => this.snapToMinSize());

    pipWindow.document.body.append(overlay);
    return overlay;
  }

  /**
   * Lưu vị trí gốc trong DOM
   */
  private saveOriginalPosition(element: HTMLElement): void {
    this.originalParent = element.parentElement;
    this.originalNextSibling = element.nextSibling;

    // Tạo placeholder giữ chỗ để khi trả về đúng vị trí
    const placeholder = document.createElement('div');
    placeholder.style.display = 'none';
    placeholder.setAttribute('data-pip-placeholder', 'true');
    this.originalParent?.insertBefore(placeholder, element);
    this.placeholder = placeholder;
  }

  /**
   * Trả element về đúng vị trí cũ trong modal DOM
   */
  private returnElementToOriginalPosition(element: HTMLElement): void {
    if (!this.originalParent || !this.placeholder) return;
    // Chèn element trở lại đúng chỗ cũ
    this.originalParent.insertBefore(element, this.placeholder);
    this.placeholder.remove();
  }

  private setupPipWindow(pipWindow: Window): void {
    const { style } = pipWindow.document.body;
    style.margin = '0';
    style.overflow = 'hidden';
    style.background = '#17212b';
    style.width = '100vw';
    style.height = '100vh';
  }

  private copyDocumentStyles(pipWindow: Window): void {
    for (const styleSheet of document.styleSheets) {
      try {
        const css = [...styleSheet.cssRules].map((rule) => rule.cssText).join('');
        const styleEl = pipWindow.document.createElement('style');
        styleEl.textContent = css;
        pipWindow.document.head.append(styleEl);
      } catch {
        // Ignore cross-origin stylesheet errors
      }
    }
  }

  private setupStyleObserver(pipWindow: Window): void {
    this.styleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeName === 'STYLE') {
            try {
              const clone = node.cloneNode(true);
              pipWindow.document.head.append(clone);
            } catch (e) {
              // ignore
            }
          }
        });
      });
    });

    this.styleObserver.observe(document.head, { childList: true });
  }

  private cleanup(pipWindow: Window): void {
    if (this.styleObserver) {
      this.styleObserver.disconnect();
      this.styleObserver = null;
    }
    if (this.resizeHandler) {
      pipWindow.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    this._pipWindow.set(null);
    this.overlay = null;
    this.placeholder = null;
    this.originalParent = null;
    this.originalNextSibling = null;
    this.typePip.set('');
  }
}
